import { useState, useCallback, useEffect, useRef } from "react";
import { isInteractiveSelectionTarget } from "../utils/selectionUtils";

export function useRepoMarqueeSelection(filteredItems, setSelectedItems) {
  const containerRef = useRef(null);
  const selectionStartRef = useRef({ x: 0, y: 0 });
  const selectionActiveRef = useRef(false);
  const dragSelectionRef = useRef(false);
  const [selectionRect, setSelectionRect] = useState(null);

  const selectItemsInRect = useCallback(
    (rect) => {
      if (!containerRef.current || !rect) return;
      const selected = new Set();
      containerRef.current
        .querySelectorAll("[data-item-id]")
        .forEach((node) => {
          const itemRect = node.getBoundingClientRect();
          const isIntersecting =
            itemRect.right >= rect.viewportLeft &&
            itemRect.left <= rect.viewportRight &&
            itemRect.bottom >= rect.viewportTop &&
            itemRect.top <= rect.viewportBottom;
          if (isIntersecting) {
            const idStr = node.dataset.itemId;
            const originalItem = filteredItems.find(
              (item) => String(item.id) === idStr,
            );
            if (originalItem) {
              selected.add(originalItem.id);
            } else {
              selected.add(idStr);
            }
          }
        });
      setSelectedItems(selected);
    },
    [filteredItems, setSelectedItems],
  );

  const updateSelection = useCallback(
    (clientX, clientY) => {
      if (!selectionActiveRef.current || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const startX = selectionStartRef.current.x;
      const startY = selectionStartRef.current.y;
      const left = Math.min(startX, clientX);
      const top = Math.min(startY, clientY);
      const width = Math.abs(clientX - startX);
      const height = Math.abs(clientY - startY);
      const rect = {
        left: left - containerRect.left,
        top: top - containerRect.top,
        width,
        height,
        viewportLeft: left,
        viewportTop: top,
        viewportRight: left + width,
        viewportBottom: top + height,
      };
      setSelectionRect(rect);
      selectItemsInRect(rect);
    },
    [selectItemsInRect],
  );

  const handleMouseDown = (event) => {
    if (event.button !== 0) return;
    if (!containerRef.current || !containerRef.current.contains(event.target))
      return;
    if (isInteractiveSelectionTarget(event.target)) return;

    const targetCard = event.target.closest(".repo-card, .repo-list-row");
    if (targetCard) {
      return;
    }

    selectionActiveRef.current = true;
    dragSelectionRef.current = false;
    selectionStartRef.current = { x: event.clientX, y: event.clientY };
    const containerRect = containerRef.current.getBoundingClientRect();
    setSelectionRect({
      left: event.clientX - containerRect.left,
      top: event.clientY - containerRect.top,
      width: 0,
      height: 0,
      right: event.clientX,
      bottom: event.clientY,
    });
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!selectionActiveRef.current) return;
      updateSelection(event.clientX, event.clientY);
      if (
        Math.abs(event.clientX - selectionStartRef.current.x) > 5 ||
        Math.abs(event.clientY - selectionStartRef.current.y) > 5
      ) {
        dragSelectionRef.current = true;
      }
    };

    const handleMouseUp = (event) => {
      if (!selectionActiveRef.current) return;
      updateSelection(event.clientX, event.clientY);
      selectionActiveRef.current = false;
      setSelectionRect(null);
      window.requestAnimationFrame(() => {
        dragSelectionRef.current = false;
      });
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [updateSelection]);

  return {
    containerRef,
    dragSelectionRef,
    selectionRect,
    handleMouseDown,
  };
}
